import * as React from 'react';
import Dropdown from 'antd/lib/dropdown';
import Avatar from 'antd/lib/avatar';
import Flex from 'antd/lib/flex';
import Text from 'antd/lib/typography/Text';
import { AppPlatform } from '../../../types';
import { antGrey, navDropdownMenuStyle } from '../../styles/globalStyles';
import { iconForPlatform } from '../../../helpers/platformComponentHelpers';
import { DropdownButtonContent } from './DropdownButtonContent';
import { HoverableNavItem } from '../shared/HoverableNavItem';

interface PlatformMenuItem {
    key: string;
    label: React.ReactNode;
}

interface PlatformDropdownProps {
    primaryPlatform: AppPlatform;
    platformDisplayText: string;
    showPlatformDropdown: boolean;
    platformMenuItems: PlatformMenuItem[];
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    onPlatformSelect: (key: string) => void;
    onSettingsClick?: () => void;
    isLegacyApp?: boolean;
    appImageUrl?: string;
    appName?: string;
}

export const PlatformDropdown: React.FC<PlatformDropdownProps> = ({
    primaryPlatform,
    platformDisplayText,
    showPlatformDropdown,
    platformMenuItems,
    isOpen,
    onOpenChange,
    onPlatformSelect,
    onSettingsClick,
    isLegacyApp,
    appImageUrl,
    appName,
}) => {
    const handleSettingsClick = React.useCallback(() => {
        onSettingsClick?.();
        onOpenChange(false);
    }, [onSettingsClick, onOpenChange]);
    const icon =
        isLegacyApp ?
            <Avatar
                shape="square"
                size={28}
                src={appImageUrl}
                style={{ borderRadius: 6, border: 'none', flexShrink: 0 }}
            >
                {appName?.charAt(0).toUpperCase()}
            </Avatar>
        :   iconForPlatform(primaryPlatform, antGrey.grey6, 28);

    const text =
        isLegacyApp ?
            <span>
                {appName} <Text style={{ color: antGrey.grey6, fontWeight: 400 }}>({platformDisplayText})</Text>
            </span>
        :   platformDisplayText;

    const content = (
        <DropdownButtonContent
            icon={icon}
            text={text}
            showCaret={showPlatformDropdown}
            cursor={showPlatformDropdown ? 'pointer' : 'default'}
        />
    );

    if (!showPlatformDropdown) {
        if (onSettingsClick) {
            return (
                <div onClick={handleSettingsClick} style={{ cursor: 'pointer' }}>
                    {content}
                </div>
            );
        }
        return content;
    }

    const dropdownContent = (
        <Flex
            vertical
            style={{
                ...navDropdownMenuStyle,
                padding: 0,
                minWidth: '25rem',
                maxHeight: '40vh',
                overflowY: 'auto',
            }}
        >
            {platformMenuItems.map((item) => (
                <HoverableNavItem
                    key={item.key}
                    onClick={() => {
                        onPlatformSelect(item.key);
                        onOpenChange(false);
                    }}
                    style={{ padding: '8px 16px', borderRadius: 'inherit' }}
                >
                    {item.label}
                </HoverableNavItem>
            ))}
        </Flex>
    );

    return (
        <Dropdown open={isOpen} onOpenChange={onOpenChange} dropdownRender={() => dropdownContent} trigger={['click']}>
            <div>{content}</div>
        </Dropdown>
    );
};
